import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useNetworkStatus from '../hooks/useNetworkStatus';
import OfflineIndicator from './OfflineIndicator';
import {
	LayoutDashboard,
	Users,
	Building2,
	UserCheck,
	Ruler,
	Palette,
	Layers,
	Menu,
	LogOut,
	ChevronLeft,
	ChevronRight,
	ShoppingBag,
	Package,
	Truck,
	Warehouse,
	Tag,
	ChevronDown,
	Database,
	CreditCard,
	MapPin,
	FileText
} from 'lucide-react';

const Layout = () => {
	const { user, logout } = useAuth();
	const location = useLocation();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [mastersOpen, setMastersOpen] = useState(false);
	const { isOnline } = useNetworkStatus();

  // Define menu items in order
  const topMenuItem = { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' };
  
  const otherMenuItems = [
    { path: '/store-manager', icon: Package, label: 'Reminders' },
    { path: '/upcoming-deliveries', icon: Truck, label: 'Upcoming Deliveries' },
    { path: '/branch-stock', icon: Warehouse, label: 'Branch Stock' },
    { path: '/listings', icon: ShoppingBag, label: 'Listings' },
    { path: '/purchased-items', icon: CreditCard, label: 'Purchased Items' }
  ];

	// Define master menu items
	const masterMenuItems = [
		{ path: '/brands', icon: Tag, label: 'Brands' },
		{ path: '/gender-masters', icon: Users, label: 'Gender Master' },
		{ path: '/simple-categories', icon: Layers, label: 'Categories (Simple)' },
		{ path: '/fabrics', icon: Layers, label: 'Fabrics' },
		{ path: '/works', icon: Layers, label: 'Work/Embroidery' },
		{ path: '/times-worn', icon: Layers, label: 'Times Worn' },
		{ path: '/condition-grades', icon: Layers, label: 'Condition Grades' },
		{ path: '/defects', icon: Layers, label: 'Defects' },
		{ path: '/roles', icon: UserCheck, label: 'Roles' },
		{ path: '/branches', icon: MapPin, label: 'Branches' },
		{ path: '/employees', icon: Users, label: 'Employees' },
		{ path: '/sizes', icon: Ruler, label: 'Sizes' },
		// { path: '/colors', icon: Palette, label: 'Colors' },
		{ path: '/categories', icon: Layers, label: 'Categories' },
		{ path: '/menus', icon: Menu, label: 'Menus' },
		{ path: '/awb-master', icon: Truck, label: 'AWB Numbers' },
		{ path: '/customer-reviews', icon: Users, label: 'Customer Reviews' },
		{ path: '/content-policies', icon: FileText, label: 'Content Policies' }
	];

  // Filter menu items based on user role
  const role = (user?.role || '').toString().toLowerCase();
  
  let showDashboard = true;
  let filteredOtherItems = otherMenuItems;
  let filteredMasterItems = masterMenuItems;
  let showMasters = false;

  if (user?.role === 'Super Admin') {
    // Super Admin sees everything
    showDashboard = true;
    filteredOtherItems = otherMenuItems;
    showMasters = true;
  } else if (user?.role === 'Store Manager') {
    showDashboard = true;
    filteredOtherItems = otherMenuItems.filter(item => 
      ['Reminders', 'Upcoming Deliveries', 'Branch Stock'].includes(item.label)
    );
    filteredMasterItems = [];
  } else if (role === 'authenticator') {
    showDashboard = true;
    filteredOtherItems = otherMenuItems.filter(item => 
      item.label === 'Branch Stock'
    );
    filteredMasterItems = [];
  } else if (user?.role === 'Customer Service') {
    showDashboard = true;
    // Customer Service sees only Listings (simplified view)
    filteredOtherItems = otherMenuItems.filter(item => 
      item.label === 'Listings'
    );
    filteredMasterItems = [];
  } else {
    // Default: Show basic items
    showDashboard = true;
    filteredOtherItems = otherMenuItems.filter(item => 
      item.label !== 'Reminders'
    );
    filteredMasterItems = [];
  }

	const handleLogout = () => {
		logout();
	};

	return (
		<div style={{ display: 'flex', minHeight: '100vh' }}>
			{/* Sidebar */}
			<div style={{
				width: sidebarCollapsed ? '70px' : '260px',
				backgroundColor: '#1f2937',
				color: 'white',
				transition: 'width 0.3s ease',
				position: 'fixed',
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				zIndex: 1000
			}}>
				{/* Header */}
				<div style={{
					padding: '20px',
					borderBottom: '1px solid #374151',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexShrink: 0
				}}>
					{!sidebarCollapsed && (
						<h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
							Hover Admin
						</h1>
					)}
					<button
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						style={{
							background: 'none',
							border: 'none',
							color: 'white',
							cursor: 'pointer',
							padding: '4px',
							borderRadius: '4px'
						}}
					>
						{sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
					</button>
				</div>

				{/* User Info */}
				{!sidebarCollapsed && (
					<div style={{
						padding: '20px',
						borderBottom: '1px solid #374151',
						flexShrink: 0
					}}>
						<div style={{ fontSize: '14px', color: '#d1d5db' }}>Welcome back,</div>
						<div style={{ fontSize: '16px', fontWeight: '600', marginTop: '4px' }}>
							{user?.name}
						</div>
						<div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
							{user?.role}
						</div>
					</div>
				)}

        {/* Navigation - Scrollable Area */}
        <nav className="sidebar-nav" style={{ 
          padding: '20px 0',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          {/* Dashboard - Always First */}
          {showDashboard && (
            <NavLink
              to={topMenuItem.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: sidebarCollapsed ? '12px 20px' : '12px 20px',
                color: location.pathname === topMenuItem.path ? '#3b82f6' : '#d1d5db',
                textDecoration: 'none',
                backgroundColor: location.pathname === topMenuItem.path ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                borderRight: location.pathname === topMenuItem.path ? '3px solid #3b82f6' : 'none',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== topMenuItem.path) {
                  e.currentTarget.style.backgroundColor = 'rgba(55, 65, 81, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== topMenuItem.path) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <topMenuItem.icon size={20} />
              {!sidebarCollapsed && (
                <span style={{ marginLeft: '12px', fontSize: '14px' }}>
                  {topMenuItem.label}
                </span>
              )}
            </NavLink>
          )}

					{/* Masters Dropdown */}
					{showMasters && (
						<>
							<button
								onClick={() => setMastersOpen(!mastersOpen)}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									width: '100%',
									padding: sidebarCollapsed ? '12px 20px' : '12px 20px',
									color: '#d1d5db',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									fontSize: '14px',
									transition: 'all 0.2s',
									marginTop: '8px'
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.backgroundColor = 'rgba(55, 65, 81, 0.5)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.backgroundColor = 'transparent';
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<Database size={20} />
									{!sidebarCollapsed && (
										<span style={{ marginLeft: '12px' }}>Masters</span>
									)}
								</div>
								{!sidebarCollapsed && (
									<ChevronDown
										size={16}
										style={{
											transform: mastersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
											transition: 'transform 0.2s'
										}}
									/>
								)}
							</button>

							{/* Master Sub-items */}
							{mastersOpen && !sidebarCollapsed && (
								<div style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
									{filteredMasterItems.map((item) => {
										const Icon = item.icon;
										const isActive = location.pathname === item.path;

										return (
											<NavLink
												key={item.path}
												to={item.path}
												style={{
													display: 'flex',
													alignItems: 'center',
													padding: '10px 20px 10px 52px',
													color: isActive ? '#3b82f6' : '#d1d5db',
													textDecoration: 'none',
													backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
													borderRight: isActive ? '3px solid #3b82f6' : 'none',
													fontSize: '13px',
													transition: 'all 0.2s'
												}}
												onMouseEnter={(e) => {
													if (!isActive) {
														e.currentTarget.style.backgroundColor = 'rgba(55, 65, 81, 0.5)';
													}
												}}
												onMouseLeave={(e) => {
													if (!isActive) {
														e.currentTarget.style.backgroundColor = 'transparent';
													}
												}}
											>
												<Icon size={16} />
												<span style={{ marginLeft: '12px' }}>
													{item.label}
												</span>
											</NavLink>
										);
									})}
								</div>
              )}
            </>
          )}

          {/* Other Menu Items - After Masters */}
          {filteredOtherItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: sidebarCollapsed ? '12px 20px' : '12px 20px',
                  color: isActive ? '#3b82f6' : '#d1d5db',
                  textDecoration: 'none',
                  backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  borderRight: isActive ? '3px solid #3b82f6' : 'none',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(55, 65, 81, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon size={20} />
                {!sidebarCollapsed && (
                  <span style={{ marginLeft: '12px', fontSize: '14px' }}>
                    {item.label}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

				{/* Logout - Fixed at Bottom */}
				<div style={{
					borderTop: '1px solid #374151',
					flexShrink: 0
				}}>
					<button
						onClick={handleLogout}
						style={{
							display: 'flex',
							alignItems: 'center',
							padding: sidebarCollapsed ? '16px 20px' : '16px 20px',
							color: '#d1d5db',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							fontSize: '14px',
							width: '100%',
							transition: 'background-color 0.2s'
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
							e.target.style.color = '#ef4444';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = 'transparent';
							e.target.style.color = '#d1d5db';
						}}
					>
						<LogOut size={20} />
						{!sidebarCollapsed && (
							<span style={{ marginLeft: '12px' }}>Logout</span>
						)}
					</button>
				</div>
			</div>

			{/* Main Content */}
			<div style={{
				marginLeft: sidebarCollapsed ? '70px' : '260px',
				flex: 1,
				transition: 'margin-left 0.3s ease'
			}}>
				{/* Top Bar */}
				<header style={{
					backgroundColor: 'white',
					padding: '16px 24px',
					borderBottom: '1px solid #e5e7eb',
					position: 'sticky',
					top: 0,
					zIndex: 100
				}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center'
					}}>
						<h2 style={{
							fontSize: '24px',
							fontWeight: '600',
							color: '#111827',
							margin: 0,
							textTransform: 'capitalize'
						}}>
							{location.pathname.slice(1) || 'Dashboard'}
						</h2>
						<div style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px'
						}}>
							{!isOnline && <OfflineIndicator />}
							<div style={{
								fontSize: '14px',
								color: '#6b7280'
							}}>
								{new Date().toLocaleDateString('en-US', {
									weekday: 'long',
									year: 'numeric',
									month: 'long',
									day: 'numeric'
								})}
							</div>
						</div>
					</div>
				</header>

				{/* Page Content */}
				<main style={{
					padding: '24px',
					backgroundColor: '#f9fafb',
					minHeight: 'calc(100vh - 73px)'
				}}>
					<Outlet />
				</main>
			</div>
		</div>
	);
};

export default Layout;